import { DEFAULT_INDICATOR_AND_OVERLAY_SYMBOL } from '@/enums/symbols';

export interface IndicatorOrOverlay {
  name: string;
  defaultParameters: string;
  sharpCharts2Id: string;
  shouldShareScaleWithParent: boolean;
  documentationURL: string;
};

export const NONE = '- None -';

export const ACCUMULATION_DISTRIBUTION: IndicatorOrOverlay = {
  name: 'Accum/Distribution Line',
  defaultParameters: '',
  sharpCharts2Id: 'ACCDIST',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/accumulation-distribution-line'
};

export const AROON: IndicatorOrOverlay = {
  name: 'Aroon',
  defaultParameters: '25',
  sharpCharts2Id: 'AROON',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/aroon'
};

export const AROON_OSCILLATOR: IndicatorOrOverlay = {
  name: 'Aroon Oscillator',
  defaultParameters: '25',
  sharpCharts2Id: 'AROONOSC',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/aroon-oscillator'
};

export const ADX_LINE: IndicatorOrOverlay = {
  name: 'ADX Line',
  defaultParameters: '14',
  sharpCharts2Id: 'ADXLINE',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/average-directional-index-adx'
};

export const ADX_LINE_WITH_PLUS_AND_MINUS_DI: IndicatorOrOverlay = {
  name: 'ADX Line (w/+DI and -DI)',
  defaultParameters: '14',
  sharpCharts2Id: 'ADX',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/average-directional-index-adx'
};

export const AVERAGE_TRUE_RANGE: IndicatorOrOverlay = {
  name: 'Avg True Range (ATR)',
  defaultParameters: '14',
  sharpCharts2Id: 'ATR',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/average-true-range-atr-and-average-true-range-percent-atrp'
};

export const BOLLINGER_BAND_WIDTH: IndicatorOrOverlay = {
  name: 'Bollinger Band Width',
  defaultParameters: '20,2',
  sharpCharts2Id: 'BBWIDTH',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/bollinger-bandwidth'
};

export const BOLLINGER_BANDS: IndicatorOrOverlay = {
  name: 'Bollinger Bands',
  defaultParameters: '20,2',
  sharpCharts2Id: 'BB',
  shouldShareScaleWithParent: true,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-overlays/bollinger-bands'
};

export const BOLLINGER_PERCENT_B: IndicatorOrOverlay = {
  name: 'Bollinger %B',
  defaultParameters: '20,2',
  sharpCharts2Id: 'BBPCT',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/b-indicator'
};

export const CHAIKIN_MONEY_FLOW: IndicatorOrOverlay = {
  name: 'Chaikin Money Flow (CMF)',
  defaultParameters: '20',
  sharpCharts2Id: 'CMF',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/chaikin-money-flow-cmf'
};

export const CHAIKIN_OSCILLATOR: IndicatorOrOverlay = {
  name: 'Chaikin Oscillator',
  defaultParameters: '3,10',
  sharpCharts2Id: 'COSC',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/chaikin-oscillator'
};

export const CHANDE_TREND_METER: IndicatorOrOverlay = {
  name: 'Chande Trend Meter',
  defaultParameters: '',
  sharpCharts2Id: 'CTM',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/chande-trend-meter-ctm'
};

export const CHANDELIER_EXITS: IndicatorOrOverlay = {
  name: 'Chandelier Exits',
  defaultParameters: '22,3.0',
  sharpCharts2Id: 'CHANDLR',
  shouldShareScaleWithParent: true,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-overlays/chandelier-exit'
};

export const COMMODITY_CHANNEL_INDEX: IndicatorOrOverlay = {
  name: 'Commodity Channel Index (CCI)',
  defaultParameters: '20',
  sharpCharts2Id: 'CCI',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/commodity-channel-index-cci'
};

export const COPPOCK_CURVE: IndicatorOrOverlay = {
  name: 'Coppock Curve',
  defaultParameters: '14,11,10',
  sharpCharts2Id: 'COPP',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/coppock-curve'
};

export const CORRELATION: IndicatorOrOverlay = {
  name: 'Correlation',
  defaultParameters: `${DEFAULT_INDICATOR_AND_OVERLAY_SYMBOL},20`,
  sharpCharts2Id: 'CORR',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/correlation-coefficient'
};

export const DATE_TIME_AXIS: IndicatorOrOverlay = {
  name: 'Date/Time Axis',
  defaultParameters: '',
  sharpCharts2Id: 'TIMESCALE',
  shouldShareScaleWithParent: false,
  documentationURL: ''
};

export const DETRENDED_PRICE_OSCILLATOR: IndicatorOrOverlay = {
  name: 'Detrended Price (DPO)',
  defaultParameters: '20',
  sharpCharts2Id: 'DPO',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/detrended-price-oscillator-dpo'
};

export const DIVIDENDS: IndicatorOrOverlay = {
  name: 'Dividends',
  defaultParameters: '',
  sharpCharts2Id: 'DIVIDENDS',
  shouldShareScaleWithParent: false,
  documentationURL: ''
};

export const EASE_OF_MOVEMENT: IndicatorOrOverlay = {
  name: 'Ease of Movement (EMV)',
  defaultParameters: '14',
  sharpCharts2Id: 'EMV',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/ease-of-movement-emv'
};

export const EVENTS: IndicatorOrOverlay = {
  name: 'Events',
  defaultParameters: 'all',
  sharpCharts2Id: 'EVENTS',
  shouldShareScaleWithParent: false,
  documentationURL: ''
};

export const EXPONENTIAL_MOVING_AVERAGE: IndicatorOrOverlay = {
  name: 'Exp. Moving Avg',
  defaultParameters: '20',
  sharpCharts2Id: 'EMA',
  shouldShareScaleWithParent: true,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-overlays/moving-averages-simple-and-exponential'
};

export const EXPONENTIAL_MOVING_AVERAGE_ENVELOPES: IndicatorOrOverlay = {
  name: 'EMA Envelopes',
  defaultParameters: '20,2.5',
  sharpCharts2Id: 'EMAENV',
  shouldShareScaleWithParent: true,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-overlays/moving-average-envelopes'
};

export const FAST_STOCHASTICS: IndicatorOrOverlay = {
  name: 'Fast Stochastics',
  defaultParameters: '14,3',
  sharpCharts2Id: 'STOFAST',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/stochastic-oscillator-fast-slow-and-full'
};

export const FORCE_INDEX: IndicatorOrOverlay = {
  name: 'Force Index',
  defaultParameters: '13',
  sharpCharts2Id: 'FORCE',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/force-index'
};

export const FULL_STOCHASTICS: IndicatorOrOverlay = {
  name: 'Full Stochastics',
  defaultParameters: '14,3,3',
  sharpCharts2Id: 'STOFULL',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/stochastic-oscillator-fast-slow-and-full'
};

export const HORIZONTAL_LINE: IndicatorOrOverlay = {
  name: 'Horizontal Line',
  defaultParameters: '',
  sharpCharts2Id: 'HL',
  shouldShareScaleWithParent: true,
  documentationURL: ''
};

export const ICHIMOKU_CLOUD: IndicatorOrOverlay = {
  name: 'Ichimoku Cloud',
  defaultParameters: '',
  sharpCharts2Id: 'ICHIMOKU2',
  shouldShareScaleWithParent: true,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-overlays/ichimoku-cloud'
};

export const ICHIMOKU_CLOUD_FULL: IndicatorOrOverlay = {
  name: 'Ichimoku Cloud (full)',
  defaultParameters: '9,26,52',
  sharpCharts2Id: 'ICHIMOKU',
  shouldShareScaleWithParent: true,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-overlays/ichimoku-cloud'
};

export const KAUFMANS_MOVING_AVERAGE: IndicatorOrOverlay = {
  name: 'Kaufman\'s Moving Avg',
  defaultParameters: '10,2,30',
  sharpCharts2Id: 'KAMA',
  shouldShareScaleWithParent: true,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-overlays/kaufmans-adaptive-moving-average-kama'
};

export const KELTNER_CHANNELS: IndicatorOrOverlay = {
  name: 'Keltner Channels',
  defaultParameters: '20,2.0,10',
  sharpCharts2Id: 'KELT',
  shouldShareScaleWithParent: true,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-overlays/keltner-channels'
};

export const MACD: IndicatorOrOverlay = {
  name: 'MACD',
  defaultParameters: '12,26,9',
  sharpCharts2Id: 'MACD',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/macd-moving-average-convergence-divergence-oscillator'
};

export const MACD_HISTOGRAM: IndicatorOrOverlay = {
  name: 'MACD Histogram',
  defaultParameters: '12,26,9',
  sharpCharts2Id: 'MACDHIST',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/macd-histogram'
};

export const MASS_INDEX: IndicatorOrOverlay = {
  name: 'Mass Index',
  defaultParameters: '25',
  sharpCharts2Id: 'MASS',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/mass-index'
};

export const MONEY_FLOW_INDEX: IndicatorOrOverlay = {
  name: 'Money Flow Index (MFI)',
  defaultParameters: '14',
  sharpCharts2Id: 'MFI',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/money-flow-index-mfi'
};

export const NEGATIVE_VOLUME_INDEX: IndicatorOrOverlay = {
  name: 'Negative Volume Index (NVI)',
  defaultParameters: '255',
  sharpCharts2Id: 'NVI',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/negative-volume-index-nvi'
};

export const ON_BALANCE_VOLUME: IndicatorOrOverlay = {
  name: 'On Balance Volume (OBV)',
  defaultParameters: '',
  sharpCharts2Id: 'OBV',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/on-balance-volume-obv'
};

export const PARABOLIC_SAR: IndicatorOrOverlay = {
  name: 'Parabolic SAR',
  defaultParameters: '0.02,0.2',
  sharpCharts2Id: 'SAR',
  shouldShareScaleWithParent: true,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-overlays/parabolic-sar'
};

export const PERCENT_PRICE_OSCILLATOR: IndicatorOrOverlay = {
  name: 'Pct Price Oscillator (PPO)',
  defaultParameters: '12,26,9',
  sharpCharts2Id: 'PPO',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/percentage-price-oscillator-ppo'
};

export const PERCENT_VOLUME_OSCILLATOR: IndicatorOrOverlay = {
  name: 'Pct Volume Oscillator (PVO)',
  defaultParameters: '12,26,9',
  sharpCharts2Id: 'PVO',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/percentage-volume-oscillator-pvo'
};

export const PIVOT_POINTS: IndicatorOrOverlay = {
  name: 'Pivot Points',
  defaultParameters: '',
  sharpCharts2Id: 'PIVOTS',
  shouldShareScaleWithParent: true,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-overlays/pivot-points'
};

export const PRICE: IndicatorOrOverlay = {
  name: 'Price',
  defaultParameters: DEFAULT_INDICATOR_AND_OVERLAY_SYMBOL,
  sharpCharts2Id: 'PRICE',
  shouldShareScaleWithParent: false,
  documentationURL: ''
};

export const PRICE_CHANNELS: IndicatorOrOverlay = {
  name: 'Price Channels',
  defaultParameters: '20',
  sharpCharts2Id: 'CHAN',
  shouldShareScaleWithParent: true,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-overlays/price-channels'
};

export const PRICE_LABELS: IndicatorOrOverlay = {
  name: 'Price Labels',
  defaultParameters: '',
  sharpCharts2Id: 'LABELS',
  shouldShareScaleWithParent: true,
  documentationURL: ''
};

export const PRICE_MOMENTUM_OSCILLATOR: IndicatorOrOverlay = {
  name: 'Price Momentum Osc. (PMO)',
  defaultParameters: '35,20,10',
  sharpCharts2Id: 'PMO',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/decisionpoint-price-momentum-oscillator-pmo'
};

export const PRICE_PERFORMANCE: IndicatorOrOverlay = {
  name: 'Price - Performance',
  defaultParameters: DEFAULT_INDICATOR_AND_OVERLAY_SYMBOL,
  sharpCharts2Id: 'PRICEPERF',
  shouldShareScaleWithParent: false,
  documentationURL: ''
};

export const PRICE_SAME_SCALE: IndicatorOrOverlay = {
  name: 'Price (same scale)',
  defaultParameters: DEFAULT_INDICATOR_AND_OVERLAY_SYMBOL,
  sharpCharts2Id: 'PRICE',
  shouldShareScaleWithParent: true,
  documentationURL: ''
};

export const PRICE_UP_DOWN_PAIR: IndicatorOrOverlay = {
  name: 'Price - Up/Down Pair',
  defaultParameters: '$NYADV,$NYDEC',
  sharpCharts2Id: 'PAIR',
  shouldShareScaleWithParent: false,
  documentationURL: ''
};

export const PRINGS_KNOW_SURE_THING: IndicatorOrOverlay = {
  name: 'Pring\'s Know Sure Thing (KST)',
  defaultParameters: '10,15,20,30,10,10,10,15,9',
  sharpCharts2Id: 'KST',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/prings-know-sure-thing-kst'
};

export const PRINGS_SPECIAL_K: IndicatorOrOverlay = {
  name: 'Pring\'s Special K',
  defaultParameters: '100,100',
  sharpCharts2Id: 'SPECIALK',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/prings-special-k'
};

export const RATE_OF_CHANGE: IndicatorOrOverlay = {
  name: 'Rate of Change (ROC)',
  defaultParameters: '12',
  sharpCharts2Id: 'ROC',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/rate-of-change-roc'
};

export const RRG_RELATIVE_STRENGTH: IndicatorOrOverlay = {
  name: 'RRG Relative Strength',
  defaultParameters: DEFAULT_INDICATOR_AND_OVERLAY_SYMBOL,
  sharpCharts2Id: 'RRG',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/rrg-relative-strength'
};

export const RSI: IndicatorOrOverlay = {
  name: 'RSI',
  defaultParameters: '14',
  sharpCharts2Id: 'RSI',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/relative-strength-index-rsi'
};

export const SCTR_LINE: IndicatorOrOverlay = {
  name: 'SCTR Line',
  defaultParameters: '',
  sharpCharts2Id: 'SCTR',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/stockcharts-technical-rank'
};

export const SIMPLE_MOVING_AVERAGE: IndicatorOrOverlay = {
  name: 'Simple Moving Avg',
  defaultParameters: '50',
  sharpCharts2Id: 'SMA',
  shouldShareScaleWithParent: true,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-overlays/moving-averages-simple-and-exponential'
};

export const SIMPLE_MOVING_AVERAGE_ENVELOPES: IndicatorOrOverlay = {
  name: 'SMA Envelopes',
  defaultParameters: '20,2.5',
  sharpCharts2Id: 'ENV',
  shouldShareScaleWithParent: true,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-overlays/moving-average-envelopes'
};

export const SLOPE_LINEAR_REGRESSION: IndicatorOrOverlay = {
  name: 'Slope (Linear Regression)',
  defaultParameters: '20',
  sharpCharts2Id: 'LINREGSLOPE',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/slope'
};

export const SLOW_STOCHASTICS: IndicatorOrOverlay = {
  name: 'Slow Stochastics',
  defaultParameters: '14,3',
  sharpCharts2Id: 'STOSLOW',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/stochastic-oscillator-fast-slow-and-full'
};

export const STANDARD_DEVIATION: IndicatorOrOverlay = {
  name: 'Standard Deviation',
  defaultParameters: '10',
  sharpCharts2Id: 'STDDEV',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/standard-deviation-volatility'
};

export const STOCHASTICS_RSI: IndicatorOrOverlay = {
  name: 'Stochastics RSI',
  defaultParameters: '14',
  sharpCharts2Id: 'STORSI',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/stochrsi'
};

export const TRIX: IndicatorOrOverlay = {
  name: 'TRIX',
  defaultParameters: '15,9',
  sharpCharts2Id: 'TRIX',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/trix'
};

export const TRUE_STRENGTH_INDEX: IndicatorOrOverlay = {
  name: 'True Strength Index (TSI)',
  defaultParameters: '25,13,7',
  sharpCharts2Id: 'TSI',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/true-strength-index'
};

export const ULCER_INDEX: IndicatorOrOverlay = {
  name: 'Ulcer Index (UI)',
  defaultParameters: '14',
  sharpCharts2Id: 'UI',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/ulcer-index'
};

export const ULTIMATE_OSCILLATOR: IndicatorOrOverlay = {
  name: 'Ultimate Oscillator',
  defaultParameters: '7,14,28',
  sharpCharts2Id: 'ULT',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/ultimate-oscillator'
};

export const VOLUME: IndicatorOrOverlay = {
  name: 'Volume',
  defaultParameters: '',
  sharpCharts2Id: 'VOL',
  shouldShareScaleWithParent: false,
  documentationURL: ''
};

export const VOLUME_BY_PRICE: IndicatorOrOverlay = {
  name: 'Volume by Price',
  defaultParameters: '',
  sharpCharts2Id: 'VOLHORIZ',
  shouldShareScaleWithParent: true,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-overlays/volume-by-price'
};

export const VORTEX_INDICATOR: IndicatorOrOverlay = {
  name: 'Vortex Indicator',
  defaultParameters: '14',
  sharpCharts2Id: 'VTX',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/vortex-indicator'
};

export const VWAP: IndicatorOrOverlay = {
  name: 'VWAP',
  defaultParameters: '',
  sharpCharts2Id: 'VWAP',
  shouldShareScaleWithParent: true,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-overlays/volume-weighted-average-price-vwap'
};

export const WILLIAMS_PERCENT_R: IndicatorOrOverlay = {
  name: 'Williams %R',
  defaultParameters: '14',
  sharpCharts2Id: 'WMR',
  shouldShareScaleWithParent: false,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-indicators/williams-r'
};

export const ZIG_ZAG: IndicatorOrOverlay = {
  name: 'ZigZag',
  defaultParameters: '',
  sharpCharts2Id: 'ZIGZAG',
  shouldShareScaleWithParent: true,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-overlays/zigzag'
};

export const ZIG_ZAG_RETRACE: IndicatorOrOverlay = {
  name: 'ZigZag (retrace)',
  defaultParameters: '',
  sharpCharts2Id: 'RETRACE',
  shouldShareScaleWithParent: true,
  documentationURL: '//chartschool.stockcharts.com/table-of-contents/technical-indicators-and-overlays/technical-overlays/zigzag'
};

export const DEFAULT_INDICATOR_OR_OVERLAY: IndicatorOrOverlay = MACD;

export const STANDARD_INDICATORS_ARRAY: IndicatorOrOverlay[] = [
  ACCUMULATION_DISTRIBUTION,
  AROON,
  AROON_OSCILLATOR,
  ADX_LINE,
  ADX_LINE_WITH_PLUS_AND_MINUS_DI,
  AVERAGE_TRUE_RANGE,
  BOLLINGER_BAND_WIDTH,
  BOLLINGER_PERCENT_B,
  COMMODITY_CHANNEL_INDEX,
  CORRELATION,
  CHAIKIN_MONEY_FLOW,
  CHAIKIN_OSCILLATOR,
  COPPOCK_CURVE,
  CHANDE_TREND_METER,
  DATE_TIME_AXIS,
  DETRENDED_PRICE_OSCILLATOR,
  DIVIDENDS,
  EASE_OF_MOVEMENT,
  FORCE_INDEX,
  MACD,
  MACD_HISTOGRAM,
  MASS_INDEX,
  MONEY_FLOW_INDEX,
  NEGATIVE_VOLUME_INDEX,
  ON_BALANCE_VOLUME,
  PERCENT_PRICE_OSCILLATOR,
  PERCENT_VOLUME_OSCILLATOR,
  PRICE,
  PRICE_PERFORMANCE,
  PRICE_UP_DOWN_PAIR,
  PRICE_MOMENTUM_OSCILLATOR,
  PRINGS_KNOW_SURE_THING,
  PRINGS_SPECIAL_K,
  RRG_RELATIVE_STRENGTH,
  RSI,
  RATE_OF_CHANGE,
  SCTR_LINE,
  SLOPE_LINEAR_REGRESSION,
  STANDARD_DEVIATION,
  FULL_STOCHASTICS,
  SLOW_STOCHASTICS,
  FAST_STOCHASTICS,
  STOCHASTICS_RSI,
  TRIX,
  TRUE_STRENGTH_INDEX,
  ULCER_INDEX,
  ULTIMATE_OSCILLATOR,
  VOLUME,
  VORTEX_INDICATOR,
  WILLIAMS_PERCENT_R
];

export const STANDARD_OVERLAYS_ARRAY: IndicatorOrOverlay[] = [
  BOLLINGER_BANDS,
  HORIZONTAL_LINE,
  ICHIMOKU_CLOUD,
  ICHIMOKU_CLOUD_FULL,
  KELTNER_CHANNELS,
  SIMPLE_MOVING_AVERAGE,
  EXPONENTIAL_MOVING_AVERAGE,
  KAUFMANS_MOVING_AVERAGE,
  SIMPLE_MOVING_AVERAGE_ENVELOPES,
  EXPONENTIAL_MOVING_AVERAGE_ENVELOPES,
  EVENTS,
  PARABOLIC_SAR,
  CHANDELIER_EXITS,
  PIVOT_POINTS,
  PRICE_CHANNELS,
  PRICE_SAME_SCALE,
  VOLUME_BY_PRICE,
  VWAP,
  ZIG_ZAG,
  ZIG_ZAG_RETRACE
];

export const OVERLAYS_OF_INDICATORS_ARRAY: IndicatorOrOverlay[] = [
  BOLLINGER_BANDS,
  BOLLINGER_BAND_WIDTH,
  COMMODITY_CHANNEL_INDEX,
  FULL_STOCHASTICS,
  HORIZONTAL_LINE,
  KELTNER_CHANNELS,
  PRINGS_KNOW_SURE_THING,
  SIMPLE_MOVING_AVERAGE,
  EXPONENTIAL_MOVING_AVERAGE,
  KAUFMANS_MOVING_AVERAGE,
  SIMPLE_MOVING_AVERAGE_ENVELOPES,
  EXPONENTIAL_MOVING_AVERAGE_ENVELOPES,
  PRICE_CHANNELS,
  PRICE_LABELS,
  RATE_OF_CHANGE,
  SLOPE_LINEAR_REGRESSION,
  STANDARD_DEVIATION,
  ZIG_ZAG,
  ZIG_ZAG_RETRACE
];

export const INDICATORS_AND_OVERLAYS_ARRAY: IndicatorOrOverlay[] = [
  ACCUMULATION_DISTRIBUTION,
  AROON,
  AROON_OSCILLATOR,
  ADX_LINE,
  ADX_LINE_WITH_PLUS_AND_MINUS_DI,
  AVERAGE_TRUE_RANGE,
  BOLLINGER_BAND_WIDTH,
  BOLLINGER_BANDS,
  BOLLINGER_PERCENT_B,
  CHAIKIN_MONEY_FLOW,
  CHAIKIN_OSCILLATOR,
  CHANDE_TREND_METER,
  CHANDELIER_EXITS,
  COMMODITY_CHANNEL_INDEX,
  COPPOCK_CURVE,
  CORRELATION,
  DATE_TIME_AXIS,
  DETRENDED_PRICE_OSCILLATOR,
  DIVIDENDS,
  EASE_OF_MOVEMENT,
  EVENTS,
  EXPONENTIAL_MOVING_AVERAGE,
  EXPONENTIAL_MOVING_AVERAGE_ENVELOPES,
  FAST_STOCHASTICS,
  FORCE_INDEX,
  FULL_STOCHASTICS,
  HORIZONTAL_LINE,
  ICHIMOKU_CLOUD,
  ICHIMOKU_CLOUD_FULL,
  KAUFMANS_MOVING_AVERAGE,
  KELTNER_CHANNELS,
  MACD,
  MACD_HISTOGRAM,
  MASS_INDEX,
  MONEY_FLOW_INDEX,
  NEGATIVE_VOLUME_INDEX,
  ON_BALANCE_VOLUME,
  PARABOLIC_SAR,
  PERCENT_PRICE_OSCILLATOR,
  PERCENT_VOLUME_OSCILLATOR,
  PIVOT_POINTS,
  PRICE,
  PRICE_CHANNELS,
  PRICE_LABELS,
  PRICE_MOMENTUM_OSCILLATOR,
  PRICE_PERFORMANCE,
  PRICE_SAME_SCALE,
  PRICE_UP_DOWN_PAIR,
  PRINGS_KNOW_SURE_THING,
  PRINGS_SPECIAL_K,
  RATE_OF_CHANGE,
  RRG_RELATIVE_STRENGTH,
  RSI,
  SCTR_LINE,
  SIMPLE_MOVING_AVERAGE,
  SIMPLE_MOVING_AVERAGE_ENVELOPES,
  SLOPE_LINEAR_REGRESSION,
  SLOW_STOCHASTICS,
  STANDARD_DEVIATION,
  STOCHASTICS_RSI,
  TRIX,
  TRUE_STRENGTH_INDEX,
  ULCER_INDEX,
  ULTIMATE_OSCILLATOR,
  VOLUME,
  VOLUME_BY_PRICE,
  VORTEX_INDICATOR,
  VWAP,
  WILLIAMS_PERCENT_R,
  ZIG_ZAG,
  ZIG_ZAG_RETRACE
];

export function getIndicatorOrOverlayObjFromIndicatorOrOverlayName(indicatorOrOverlayName: string): IndicatorOrOverlay {
  return INDICATORS_AND_OVERLAYS_ARRAY.find(option => option.name === indicatorOrOverlayName) ?? DEFAULT_INDICATOR_OR_OVERLAY;
}
